/**
 * lib/grading.ts
 * ------------------------------------------------------------------
 * Pure grading logic for the LKC Academic Platform.
 *
 * FEATURE #8 — term-aware marks
 *  Term 1:  report shows the term mark (= average of monthly CA);
 *           grade is derived from the term mark.
 *  Term 2/3: report shows the CA/term mark AND a separate exam mark;
 *           grade is derived from the EXAM mark.
 *
 * Form-level rules preserved from v1:
 *  - Pass mark is 60% for ALL forms, school-wide (Aug 2026 policy;
 *    previously Forms 1–4 passed at 50%)
 *  - Grades are displayed for ALL forms (Aug 2026 policy; juniors
 *    were previously grade-free)
 *  - 'ABS' is a valid stored mark meaning Absent
 * ------------------------------------------------------------------
 */
import type {
  GradeBoundary,
  MaybeMark,
  MarkValue,
  CAMonths,
  SubjectMarkEntry,
  Subject,
} from '@/types';

export const GRADE_BOUNDARIES: GradeBoundary[] = [
  { grade: 'A*', min: 90, max: 100, color: [22, 101, 52] },
  { grade: 'A', min: 80, max: 89, color: [34, 197, 94] },
  { grade: 'B', min: 70, max: 79, color: [37, 99, 196] },
  { grade: 'C', min: 60, max: 69, color: [45, 82, 149] },
  { grade: 'D', min: 50, max: 59, color: [212, 169, 44] },
  { grade: 'E', min: 45, max: 49, color: [249, 115, 22] },
  { grade: 'F', min: 40, max: 44, color: [234, 88, 12] },
  { grade: 'G', min: 35, max: 39, color: [194, 65, 12] },
  { grade: 'U', min: 0, max: 34, color: [220, 38, 38] },
];

/* ------------------------------------------------------------------
   LKC grading schemes
   ------------------------------------------------------------------
   The scale above (GRADE_BOUNDARIES) is LKC's confirmed full 9-grade
   EXTENDED scale:
     A* 90-100, A 80-89, B 70-79, C 60-69, D 50-59,
     E 45-49, F 40-44, G 35-39, U 0-34.

   Subjects sat on the CORE tier are capped at C — a Core candidate can
   never be awarded above a C, however high the mark. So the Core 'C'
   band spans the whole 60-100 range (any mark Extended would grade
   A*..C becomes a Core C). Core bands below C match the Extended scale:
     C 60-100, D 50-59, E 45-49, F 40-44, G 35-39, U 0-34.

   GRADE_BOUNDARIES stays the authoritative EXTENDED export (many
   modules import it directly). Callers that know a subject's tier
   resolve the right set via boundariesForScheme(scheme).
   ------------------------------------------------------------------ */

export type GradeScheme = 'extended' | 'core';

/** Default tier used everywhere a scheme isn't explicitly supplied. */
export const DEFAULT_GRADE_SCHEME: GradeScheme = 'extended';

/** Core tier — capped at C (60-100), awardable down to G/U. */
export const CORE_GRADE_BOUNDARIES: GradeBoundary[] = [
  { grade: 'C', min: 60, max: 100, color: [45, 82, 149] },
  { grade: 'D', min: 50, max: 59, color: [212, 169, 44] },
  { grade: 'E', min: 45, max: 49, color: [249, 115, 22] },
  { grade: 'F', min: 40, max: 44, color: [234, 88, 12] },
  { grade: 'G', min: 35, max: 39, color: [194, 65, 12] },
  { grade: 'U', min: 0, max: 34, color: [220, 38, 38] },
];

/** Resolve the boundary table for a grading scheme (default: extended). */
export function boundariesForScheme(
  scheme: GradeScheme = DEFAULT_GRADE_SCHEME,
): GradeBoundary[] {
  return scheme === 'core' ? CORE_GRADE_BOUNDARIES : GRADE_BOUNDARIES;
}

/** A subject's grading tier. Absent field => extended (historic default). */
export function schemeForSubject(
  subject: Pick<Subject, 'gradeScheme'> | null | undefined,
): GradeScheme {
  return subject?.gradeScheme === 'core' ? 'core' : DEFAULT_GRADE_SCHEME;
}

/**
 * Every grade letter that can appear across BOTH tiers, in descending
 * attainment order (A*, A, B, C, D, E, F, G, U). Derived from the
 * boundary tables (sorted by band floor) so it can't drift from them.
 * Analysis buckets, CSV columns and grade charts iterate this union so
 * Core-only grades (F, G) don't silently vanish from reports.
 */
export const GRADE_ORDER_ALL: string[] = (() => {
  const floor = new Map<string, number>();
  for (const b of [...GRADE_BOUNDARIES, ...CORE_GRADE_BOUNDARIES]) {
    const prev = floor.get(b.grade);
    if (prev === undefined || b.min > prev) floor.set(b.grade, b.min);
  }
  return [...floor.keys()].sort((a, b) => floor.get(b)! - floor.get(a)!);
})();

/**
 * Months a term's continuous assessment can span. LKC terms run ~3
 * months (see school.config). These are the CA columns shown in
 * Mark Entry; teachers fill the months that have happened.
 */
export const TERM_CA_MONTHS: Record<string, string[]> = {
  Term1: ['January', 'February', 'March', 'April'],
  Term2: ['May', 'June', 'July', 'August'],
  Term3: ['September', 'October', 'November', 'December'],
};

/** Does this term use a separate exam mark? (T2 & T3 only.) */
export function termUsesExam(term: string): boolean {
  return term === 'Term2' || term === 'Term3';
}

/* ------------------------------------------------------------------
   Mark value helpers
   ------------------------------------------------------------------ */

export function isAbsent(m: MaybeMark | null | undefined): boolean {
  if (m === null || m === undefined || m === '') return false;
  return String(m).toUpperCase() === 'ABS';
}

export function isBlank(m: MaybeMark | null | undefined): boolean {
  return m === null || m === undefined || m === '';
}

/** Coerce arbitrary input into a stored MarkValue or '' (blank). */
export function normaliseMark(value: string | null | undefined): MarkValue | '' {
  if (value === '' || value === null || value === undefined) return '';
  const str = String(value);
  const upper = str.toUpperCase().trim();
  if (upper === 'A' || upper === 'AB' || upper === 'ABS') return 'ABS';
  if (/^\d+$/.test(str.trim())) {
    return Math.min(100, Math.max(0, parseInt(str, 10)));
  }
  return ''; // invalid -> treat as blank (caller may ignore)
}

/* ------------------------------------------------------------------
   Continuous-assessment average
   ------------------------------------------------------------------ */

/**
 * Average of the entered numeric CA months. 'ABS' months count as 0
 * (a missed assessment scores zero); blank months are ignored.
 * Returns null if no months have been entered at all.
 */
/**
 * Average of the entered numeric CA months. Per LKC policy, an 'ABS'
 * month is IGNORED (averaged over the months that are present), not
 * counted as zero. Blank months are also ignored.
 *
 * Returns null if no numeric months exist (all blank, or only ABS).
 */
export function computeCaMark(ca: CAMonths | undefined): number | null {
  if (!ca) return null;
  // Keep only months that hold a real numeric mark, as numbers.
  const numeric: number[] = [];
  for (const v of Object.values(ca)) {
    if (isBlank(v) || isAbsent(v)) continue;
    const n = typeof v === 'number' ? v : Number(v);
    if (!Number.isNaN(n)) numeric.push(n);
  }
  if (numeric.length === 0) return null;

  const sum = numeric.reduce((acc, n) => acc + n, 0);
  return Math.round(sum / numeric.length);
}

/* ------------------------------------------------------------------
   Term-aware derivation — the core of feature #8
   ------------------------------------------------------------------ */

export interface DerivedMark {
  caMark: number | null;
  exam: MaybeMark | undefined;
  /** Primary "term mark" shown on the report. */
  termMark: number | null;
  /** Value the grade is computed from (term mark in T1, exam in T2/3). */
  gradeSource: MaybeMark | null;
  reportGrade: string;
}

/**
 * Given the raw CA months + optional exam for a term, derive every
 * value the report needs. This is the single place term logic lives.
 */
export function deriveMark(
  term: string,
  ca: CAMonths | undefined,
  exam: MaybeMark | undefined,
  scheme: GradeScheme = DEFAULT_GRADE_SCHEME,
): DerivedMark {
  const caMark = computeCaMark(ca);

  if (!termUsesExam(term)) {
    // ---- TERM 1: term mark = CA average; grade from term mark ----
    return {
      caMark,
      exam: undefined,
      termMark: caMark,
      gradeSource: caMark,
      reportGrade: gradeFor(caMark, scheme),
    };
  }

  // ---- TERM 2 & 3: show CA/term mark + separate exam; grade from exam ----
  let gradeSource: MaybeMark | null;
  if (isAbsent(exam)) gradeSource = 'ABS';
  else if (isBlank(exam)) gradeSource = null;
  else gradeSource = Number(exam);

  return {
    caMark,
    exam,
    termMark: caMark, // the "term/CA mark" column on the report
    gradeSource,
    reportGrade: gradeFor(gradeSource, scheme),
  };
}

/** Build a complete SubjectMarkEntry from raw CA + exam + comment. */
export function buildMarkEntry(
  term: string,
  ca: CAMonths,
  exam: MaybeMark | undefined,
  comment: string,
  meta?: { updatedBy?: string },
  scheme: GradeScheme = DEFAULT_GRADE_SCHEME,
): SubjectMarkEntry {
  const d = deriveMark(term, ca, exam, scheme);
  return {
    ca,
    caMark: d.caMark,
    exam: termUsesExam(term) ? exam : undefined,
    termMark: d.termMark,
    reportGrade: d.reportGrade,
    comment,
    updatedAt: new Date().toISOString(),
    updatedBy: meta?.updatedBy,
  };
}

/* ------------------------------------------------------------------
   Grade lookup
   ------------------------------------------------------------------ */

export function gradeFor(
  mark: MaybeMark | null,
  scheme: GradeScheme = DEFAULT_GRADE_SCHEME,
): string {
  if (mark === null || mark === undefined || mark === '') return '-';
  if (isAbsent(mark)) return 'ABS';
  const n = typeof mark === 'number' ? mark : parseInt(String(mark), 10);
  if (Number.isNaN(n)) return '-';
  const b = boundariesForScheme(scheme).find((x) => n >= x.min && n <= x.max);
  return b ? b.grade : 'U';
}

export function gradeColor(grade: string): [number, number, number] {
  if (grade === 'ABS') return [100, 116, 139];
  if (grade === '-') return [107, 114, 128];
  // Colour is keyed by grade LETTER, which is scheme-independent: the two
  // tiers share identical colours for C/D/E/U, and F/G are Core-only. So a
  // union lookup resolves every letter without threading a scheme through
  // the (display-only) call sites. Extended boundaries first (the common
  // case), then Core to pick up F/G.
  const found =
    GRADE_BOUNDARIES.find((b) => b.grade === grade) ??
    CORE_GRADE_BOUNDARIES.find((b) => b.grade === grade);
  return found?.color ?? [107, 114, 128];
}

/* ------------------------------------------------------------------
   Form-level rules (preserved from v1)
   ------------------------------------------------------------------ */

export function getFormLevel(classIdOrName: string | undefined): string | null {
  if (!classIdOrName) return null;
  const upper = classIdOrName.toUpperCase();
  if (upper.startsWith('AS')) return 'AS';
  if (upper.startsWith('A2')) return 'A2';
  const m = classIdOrName.match(/Form\s*(\d)/i);
  if (m) return m[1];
  const n = classIdOrName.match(/(\d)/);
  return n ? n[1] : null;
}

export function getPassMark(_formLevel: string | null): number {
  // Policy (Aug 2026): the pass mark is 60% for EVERY form, school-wide.
  // Previously Forms 1–4 passed at 50% and Forms 5/6/AS/A2 at 60%; the
  // school standardised on 60% across the board. Single source — this
  // flows to every analysis, at-risk calc, and report card.
  return 60;
}

export function shouldShowGrades(_formLevel: string | null): boolean {
  // Policy (Aug 2026): grades / grade symbols are shown for ALL forms,
  // Form 1 & 2 included. Previously juniors were grade-free (the v1 rule),
  // but the school now wants a grade against every mark on the report,
  // the live academic view, and the mark-entry grid.
  return true;
}

export function isMarkFailing(
  mark: MaybeMark | null,
  formLevel: string | null,
): boolean {
  if (isBlank(mark) || isAbsent(mark)) return false;
  const n = typeof mark === 'number' ? mark : parseInt(String(mark), 10);
  if (Number.isNaN(n)) return false;
  return n < getPassMark(formLevel);
}

export interface PassResult {
  passed: boolean;
  failedCount: number;
  englishFailed: boolean;
  reason?: string;
}

/**
 * LKC pass criteria (parity with v1): at most `maxFailures` subjects
 * below the pass mark, and English must be passed.
 */
export function evaluatePass(
  subjectMarks: { subjectId: string; mark: MaybeMark | null }[],
  formLevel: string | null,
  opts: { maxFailures?: number; englishSubjectId?: string } = {},
): PassResult {
  const maxFailures = opts.maxFailures ?? 1;
  const englishId = opts.englishSubjectId ?? 'english';

  const numeric = subjectMarks.filter(
    (s) => !isBlank(s.mark) && !isAbsent(s.mark),
  );
  const failedCount = numeric.filter((s) =>
    isMarkFailing(s.mark, formLevel),
  ).length;

  const eng = subjectMarks.find((s) => s.subjectId === englishId);
  const englishFailed =
    !!eng &&
    !isBlank(eng.mark) &&
    !isAbsent(eng.mark) &&
    isMarkFailing(eng.mark, formLevel);

  if (englishFailed) {
    return { passed: false, failedCount, englishFailed, reason: 'Failed English' };
  }
  if (failedCount > maxFailures) {
    return {
      passed: false,
      failedCount,
      englishFailed,
      reason: `Failed ${failedCount} subjects (max ${maxFailures})`,
    };
  }
  return { passed: true, failedCount, englishFailed };
}

export default {
  GRADE_BOUNDARIES,
  CORE_GRADE_BOUNDARIES,
  GRADE_ORDER_ALL,
  DEFAULT_GRADE_SCHEME,
  boundariesForScheme,
  schemeForSubject,
  TERM_CA_MONTHS,
  termUsesExam,
  isAbsent,
  isBlank,
  normaliseMark,
  computeCaMark,
  deriveMark,
  buildMarkEntry,
  gradeFor,
  gradeColor,
  getFormLevel,
  getPassMark,
  shouldShowGrades,
  isMarkFailing,
  evaluatePass,
};
